/**
 * Park icons — maps Disney park names to Lucide icon components.
 * Provides a fallback (Castle) for any park not explicitly configured.
 *
 * When a new park is added, add it here and in PARK_ICONS_LIST below.
 */
import {
  Cog,
  Castle,
  Clapperboard,
  Cat,
  LucideIcon,
  Film,
  TreePalm,
  Rotate3D,
  Dot,
  RollerCoaster,
  SunSnow,
  CloudSunRain,
  Mountain
} from 'lucide-react';

/** A single park entry: name → Lucide icon + short abbreviation used in calendar cells. */
export interface ParkIconDef {
  name: string;
  icon: LucideIcon;
  abbr: string;
}

const DEFAULT_ICON: LucideIcon = RollerCoaster;

/** Returns the Lucide icon component for a park name. Falls back to Castle. */
export function getParkIcon(parkName: string): LucideIcon {
  return parkName === "Unknown" ? Dot : (PARK_ICONS[parkName] ?? DEFAULT_ICON);
}

/** Returns the short abbreviation (e.g. "MK") for a park name. Falls back to the name itself. */
export function getParkAbbr(parkName: string): string {
  return PARK_ICONS_LIST.find((p) => p.name === parkName)?.abbr ?? parkName;
}

/** Ordered list of configured parks — single source of truth for icons and abbreviations. */
export const PARK_ICONS_LIST: ParkIconDef[] = [
  { name: 'Magic Kingdom', icon: Castle, abbr: 'Magic Kingdom' },
  { name: 'Epcot', icon: Cog, abbr: 'Epcot' },
  { name: "Disney's Hollywood Studios", icon: Clapperboard, abbr: 'Hollywood Studio' },
  { name: "Hollywood Studios", icon: Clapperboard, abbr: 'Hollywood Studio' },
  { name: 'Animal Kingdom', icon: Cat, abbr: 'Anmal Kingdom' },
  { name: "Universal Studio Florida", icon: Film, abbr: 'Universal Studio' },
  { name: "Universal Epic Universe", icon: Rotate3D, abbr: 'Epic Universe' },
  { name: "Universal Islands of Adventures", icon: TreePalm, abbr: 'Islands of Adventures' },
  { name: "Blizzard Beach", icon: SunSnow, abbr: 'Blizzard Beach' },
  { name: "Typhoon lagoon", icon: CloudSunRain, abbr: 'Typhoon Lagoon' },
  { name: "Volcano Bay", icon: Mountain, abbr: 'Volcano Bay' }
];

/** Build PARK_ICONS lookup from PARK_ICONS_LIST. Ensures icon maps stay in sync. */
const PARK_ICONS: Record<string, LucideIcon> = Object.fromEntries(
  PARK_ICONS_LIST.map(({ name, icon }) => [name, icon]),
) as Record<string, LucideIcon>;
